import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Heart } from "lucide-react";

const MULTI_DAY_EVENT_IDS = [4];

export default function EventsPage() {
  const cardRefs = useRef([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // fetch events from backend
  useEffect(() => {
    async function loadEvents() {
      try {
        const base = process.env.REACT_APP_API_BASE; // CRA
        // const base = import.meta.env.VITE_API_BASE; // Vite

        const res = await fetch(`${base}/api/events`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setEvents(data.events || []);
      } catch (e) {
        console.error("Failed to load events:", e);
      } finally {
        setLoading(false);
      }
    }
    loadEvents();
  }, []);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("fade-in-visible");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    cardRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, [events]);

  if (loading) return <div className="p-8">Loading events...</div>;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#faf8f5" }}>
      {/* keep ALL your original styles + background stuff here */}
      <style>{`
        .fade-in-card { opacity:0; transform: translateY(30px); transition: opacity .6s ease-out, transform .6s ease-out; }
        .fade-in-visible { opacity:1; transform: translateY(0); }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 py-12 relative z-10">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Events & Activities</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join us for exciting events and support our community
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, index) => {
            const title = event.title;
            const img = event.image || "";
            const summary = event.summary || "";

            const start = event.date ? new Date(event.date) : null;
            const end = start ? new Date(start) : null;
            if (end) end.setDate(start.getDate() + 1);

            const dateText = start
              ? MULTI_DAY_EVENT_IDS.includes(event.id)
                ? `${start.getDate()} & ${end.getDate()} ${start.toLocaleString('en-SG', { month: 'long', year: 'numeric' })}`
                : start.toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })
              : "";

            const link = `/event/${event.id}`;

            return (
              <Link to={link} key={event.id} className="block">
                <div
                  ref={(el) => (cardRefs.current[index] = el)}
                  className="fade-in-card relative rounded-3xl overflow-hidden shadow-xl group transform transition-all duration-500 hover:scale-105 hover:shadow-2xl bg-white cursor-pointer"
                >
                  {!!img && (
                    <img
                      src={img}
                      alt={title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent opacity-80"></div>

                  <div className="relative h-[400px] flex flex-col justify-between p-8">
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-3 shadow-lg w-fit">
                      <Calendar className="w-8 h-8 text-gray-700" />
                    </div>

                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">
                        {title}
                      </h2>

                      {summary && (
                        <p className="text-white/90 mb-2 text-sm drop-shadow-md">
                          {summary}
                        </p>
                      )}

                      {dateText && (
                        <p className="text-white/80 mb-4 text-sm flex items-center gap-2 drop-shadow-md">
                          <Calendar className="w-4 h-4" />
                          {dateText}
                        </p>
                      )}


                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}